import { Box } from '@mui/material';
import React from 'react';

import { CardBack } from '../art/CardArt';

interface Props {
    /** True while the deck is being shuffled: the halves riffle together over and over. */
    shuffling: boolean;
    onTouch: () => void;
    label: string;
}

const CARDS = 16;
const WIDTH = 124;
const HEIGHT = Math.round((WIDTH * 340) / 200);

/**
 * A stack of cards that the person touches to begin. While `shuffling`, the stack splits in two
 * halves that bend and riffle back together, one card after another, like a hand shuffle.
 * Cards alternate between the halves, so the cascade interleaves them.
 */
export const ShuffleDeck: React.FC<Props> = ({ shuffling, onTouch, label }) => (
    <Box
        component="button"
        type="button"
        onClick={onTouch}
        disabled={shuffling}
        aria-label={label}
        data-testid="shuffle-deck"
        sx={{
            position: 'relative',
            width: WIDTH + 150,
            height: HEIGHT + 30,
            my: 2,
            p: 0,
            border: 0,
            background: 'none',
            cursor: shuffling ? 'default' : 'pointer',
            filter: shuffling
                ? 'drop-shadow(0 0 36px rgba(212,175,106,.65))'
                : 'drop-shadow(0 0 22px rgba(157,123,216,.45))',
            transition: 'filter 1.2s',
            '@keyframes arcana-riffle': {
                '0%': { transform: 'translate(0, var(--lift)) rotate(0deg)' },
                '24%': {
                    transform:
                        'translate(calc(var(--s) * 70px), -4px) rotate(calc(var(--s) * 6deg))',
                },
                '52%': {
                    transform:
                        'translate(calc(var(--s) * 70px), -16px) rotate(calc(var(--s) * 12deg))',
                },
                '80%': {
                    transform:
                        'translate(calc(var(--s) * 8px), calc(var(--lift) - 14px)) rotate(calc(var(--s) * 2deg))',
                },
                '100%': { transform: 'translate(0, var(--lift)) rotate(0deg)' },
            },
            '@keyframes arcana-idle': {
                '0%, 100%': { transform: 'translate(0, var(--lift))' },
                '50%': { transform: 'translate(0, calc(var(--lift) - 5px))' },
            },
        }}
    >
        {Array.from({ length: CARDS }, (_, i) => {
            const side = i % 2 === 0 ? -1 : 1;
            return (
                <Box
                    key={i}
                    aria-hidden="true"
                    style={
                        {
                            '--s': side,
                            '--lift': `${-i * 1.5}px`,
                            zIndex: i,
                            animationDelay: shuffling
                                ? `${i * 30}ms`
                                : `${i * 60}ms`,
                        } as React.CSSProperties
                    }
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: 15,
                        width: WIDTH,
                        height: HEIGHT,
                        ml: `${-WIDTH / 2}px`,
                        borderRadius: '10px',
                        boxShadow: '0 2px 8px rgba(0,0,0,.6)',
                        transform: 'translate(0, var(--lift))',
                        animation: shuffling
                            ? 'arcana-riffle 1.1s ease-in-out infinite'
                            : 'arcana-idle 3.6s ease-in-out infinite',
                        '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                        },
                    }}
                >
                    <CardBack />
                </Box>
            );
        })}
    </Box>
);
